// Single-elimination + consolation playoff bracket topology.
//
// Single source of truth for playoff bracket geometry: slot keys/labels,
// first-round seed pairings, and the advancement wiring ("winner of QF1 feeds
// Semifinal 1's home side"). Consumed by:
//   - apps/admin (DivisionPage bracket scheduler, auto-populate, PDF scoresheet)
//   - apps/api (game.controller advancement engine on score save)
//
// Slot keys for the winners bracket are byte-identical to the legacy
// apps/admin/src/utils/playoffSlots.ts keys (quarterfinal-1..4, semifinal-1/2,
// championship, third_place) so pre-existing games — which may carry only
// `competitionMeta.stage` — keep resolving via the `bracketSlot ?? stage`
// fallback. Consolation adds consolation-semifinal-1/2, fifth_place,
// seventh_place.

export type BracketSide = 'home' | 'away';

export interface SlotFeed {
  slot: string;
  side: BracketSide;
}

export interface BracketSlotDef {
  // Canonical unique key == competitionMeta.bracketSlot == DOM data-slot-id.
  key: string;
  label: string;
  // competitionMeta.stage value.
  stage: string;
  // Top-level game.playoffRound value (drives the public Playoff Results view).
  playoffRound: string;
  // Visual grouping / scheduler column header.
  group: string;
  // First-round slots only: [highSeed, lowSeed] fed from the seed order.
  seeds?: [number, number];
  // Where this game's winner / loser advance to.
  winnerTo?: SlotFeed;
  loserTo?: SlotFeed;
}

// Division config.format values that use a standalone bracket (as opposed to
// round-robin/pool formats whose playoff column is appended to a group stage).
export const BRACKET_FORMATS = ['playoffs', 'bracket', 'playoffs-consolation'] as const;

export function isBracketFormat(format?: string | null): boolean {
  return !!format && (BRACKET_FORMATS as readonly string[]).includes(format);
}

// Formats that carry a playoff bracket at all — the standalone bracket formats
// plus the hybrid group-stage formats whose playoff column is appended after
// round-robin / pool play. Games in any of these may carry
// `competitionMeta.bracketSlot` and participate in auto-advancement.
export const PLAYOFF_BRACKET_FORMATS = [
  ...BRACKET_FORMATS,
  'round-robin-playoffs',
  'pool-play-playoffs',
] as const;

export function hasPlayoffBracket(format?: string | null): boolean {
  return !!format && (PLAYOFF_BRACKET_FORMATS as readonly string[]).includes(format);
}

// Placeholder text for a first-round side whose team isn't known yet —
// standalone brackets before enough teams are registered, and hybrid formats
// where round 1 is filled from group-stage standings.
export function seedPlaceholder(seed: number): string {
  return `Seed ${seed}`;
}

export function isConsolationFormat(format?: string | null): boolean {
  return format === 'playoffs-consolation';
}

// Normalize an arbitrary stored playoffTeams value to a supported size.
// Consolation has no meaningful 2-team bracket, so 2 coerces to 4.
export function normalizeBracketSize(size: number | undefined, consolation: boolean): 2 | 4 | 8 {
  if (size === 8) return 8;
  if (size === 2) return consolation ? 4 : 2;
  return 4;
}

const feed = (slot: string, side: BracketSide): SlotFeed => ({ slot, side });

const FINAL: BracketSlotDef = {
  key: 'championship',
  // Display name is "Championship" (Nikkei organizer ask 2026-10-02, INT-0031);
  // the slot KEY/round stay 'championship', and the game-STATUS chip "Final"
  // (= game over) is a different concept that keeps its name.
  label: 'Championship',
  stage: 'championship',
  playoffRound: 'championship',
  group: 'Championship',
};

const THIRD: BracketSlotDef = {
  key: 'third_place',
  label: '3rd Place',
  stage: 'third_place',
  playoffRound: 'third_place',
  group: '3rd Place',
};

const FIFTH: BracketSlotDef = {
  key: 'fifth_place',
  label: '5th Place',
  stage: 'fifth_place',
  playoffRound: 'fifth_place',
  group: '5th Place',
};

const SEVENTH: BracketSlotDef = {
  key: 'seventh_place',
  label: '7th Place',
  stage: 'seventh_place',
  playoffRound: 'seventh_place',
  group: '7th Place',
};

function semis(seeds4?: boolean): BracketSlotDef[] {
  return [1, 2].map((i) => ({
    key: `semifinal-${i}`,
    label: `Semifinal ${i}`,
    stage: 'semifinal',
    playoffRound: 'semifinal',
    group: 'Semifinals',
    ...(seeds4 ? { seeds: (i === 1 ? [1, 4] : [2, 3]) as [number, number] } : {}),
    winnerTo: feed('championship', i === 1 ? 'home' : 'away'),
    loserTo: feed('third_place', i === 1 ? 'home' : 'away'),
  }));
}

const QF_SEEDS: [number, number][] = [[1, 8], [4, 5], [3, 6], [2, 7]];

function quarters(consolation: boolean): BracketSlotDef[] {
  return [1, 2, 3, 4].map((i) => ({
    key: `quarterfinal-${i}`,
    label: `Quarterfinal ${i}`,
    stage: 'quarterfinal',
    playoffRound: 'quarterfinal',
    group: 'Quarterfinals',
    seeds: QF_SEEDS[i - 1],
    winnerTo: feed(`semifinal-${i <= 2 ? 1 : 2}`, i % 2 === 1 ? 'home' : 'away'),
    ...(consolation
      ? { loserTo: feed(`consolation-semifinal-${i <= 2 ? 1 : 2}`, i % 2 === 1 ? 'home' : 'away') }
      : {}),
  }));
}

function consolationSemis(): BracketSlotDef[] {
  return [1, 2].map((i) => ({
    key: `consolation-semifinal-${i}`,
    label: `Consolation Semifinal ${i}`,
    stage: 'consolation_semifinal',
    playoffRound: 'consolation_semifinal',
    group: 'Consolation Semifinals',
    winnerTo: feed('fifth_place', i === 1 ? 'home' : 'away'),
    loserTo: feed('seventh_place', i === 1 ? 'home' : 'away'),
  }));
}

// Ordered bracket slots (earliest round first) for a size + format.
export function getBracketSlots(size?: number, consolation = false): BracketSlotDef[] {
  const n = normalizeBracketSize(size, consolation);
  if (n === 2) {
    return [{ ...FINAL, seeds: [1, 2] }];
  }
  if (n === 4) {
    return [...semis(true), FINAL, THIRD];
  }
  if (!consolation) {
    return [...quarters(false), ...semis(), FINAL, THIRD];
  }
  return [...quarters(true), ...consolationSemis(), ...semis(), FINAL, THIRD, FIFTH, SEVENTH];
}

export function getSlotDef(
  size: number | undefined,
  consolation: boolean,
  slotKey: string,
): BracketSlotDef | undefined {
  return getBracketSlots(size, consolation).find((s) => s.key === slotKey);
}

// Placeholder text for a side of a slot fed by an earlier game, e.g.
// "Winner of Quarterfinal 1" / "Loser of Semifinal 2". Undefined for
// seed-fed (first-round) sides.
export function placeholderFromSlots(
  slots: BracketSlotDef[],
  slotKey: string,
  side: BracketSide,
): string | undefined {
  for (const s of slots) {
    if (s.winnerTo && s.winnerTo.slot === slotKey && s.winnerTo.side === side) {
      return `Winner of ${s.label}`;
    }
    if (s.loserTo && s.loserTo.slot === slotKey && s.loserTo.side === side) {
      return `Loser of ${s.label}`;
    }
  }
  return undefined;
}

export function placeholderForSide(
  size: number | undefined,
  consolation: boolean,
  slotKey: string,
  side: BracketSide,
): string | undefined {
  return placeholderFromSlots(getBracketSlots(size, consolation), slotKey, side);
}

// Standard single-elimination first-round seed pairings ([highSeed, lowSeed])
// so the top seeds can only meet in the final.
export function bracketSeedPairs(size?: number, consolation = false): [number, number][] {
  return getBracketSlots(size, consolation)
    .filter((s) => s.seeds)
    .map((s) => s.seeds as [number, number]);
}
